import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { VerisureQrCard } from '../../components/verisure/VerisureQrCard';
import { toast } from 'react-hot-toast';

export const VerisureQrGeneratorPage: React.FC = () => {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState('https://verisure.online/asset/VRS-001');
  const [customAssetId, setCustomAssetId] = useState('VRS-001');
  const [title, setTitle] = useState('Verisure Hardware Asset');
  const [subtitle, setSubtitle] = useState('Certified Equipment');
  const [preset, setPreset] = useState<'default' | 'item' | 'cabinet' | 'custom'>('default');

  const handlePresetChange = (newPreset: 'default' | 'item' | 'cabinet' | 'custom') => {
    setPreset(newPreset);
    const origin = window.location.origin;

    if (newPreset === 'default') {
      setInputValue('https://verisure.online/asset/VRS-001');
      setCustomAssetId('VRS-001');
      setTitle('Verisure Hardware Asset');
      setSubtitle('Certified Equipment');
    } else if (newPreset === 'item') {
      const randomId = `ITM-${Math.floor(1000 + Math.random() * 9000)}`;
      setInputValue(`${origin}/item/${randomId}`);
      setCustomAssetId(randomId);
      setTitle('Optical Time-Domain Reflectometer');
      setSubtitle('Telecom Equipment');
    } else if (newPreset === 'cabinet') {
      const randomId = `CAB-${Math.floor(100 + Math.random() * 900)}`;
      setInputValue(`${origin}/verisure/cabinet/${randomId}`);
      setCustomAssetId(randomId);
      setTitle('Main Distribution Frame');
      setSubtitle('Core Network Cabinet');
    } else {
      setInputValue(`${origin}/item/NEW-ASSET`);
      setCustomAssetId('NEW-ASSET');
      setTitle('');
      setSubtitle('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    // Auto-update asset ID from end of URL if user didn't manually customize
    const extracted = val.split('/').filter(Boolean).pop();
    if (extracted && preset !== 'custom') {
      setCustomAssetId(extracted);
    }
  };

  const handleGenerateNewRandom = () => {
    if (preset === 'item') {
      const randomId = `ITM-${Math.floor(1000 + Math.random() * 9000)}`;
      setInputValue(`${window.location.origin}/item/${randomId}`);
      setCustomAssetId(randomId);
    } else if (preset === 'cabinet') {
      const randomId = `CAB-${Math.floor(100 + Math.random() * 900)}`;
      setInputValue(`${window.location.origin}/verisure/cabinet/${randomId}`);
      setCustomAssetId(randomId);
    } else {
      const randomId = `VRS-${Math.floor(100 + Math.random() * 900)}`;
      setInputValue(`https://verisure.online/asset/${randomId}`);
      setCustomAssetId(randomId);
    }
    toast.success('Generated new unique Asset ID & QR');
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto font-sans space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill-container text-orange-400 text-[11px] font-bold rounded-full mb-3 shadow-sm border border-orange-500/25">
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></span>
            <span className="tracking-wider uppercase">OFFICIAL IDENTITY CARD ENGINE</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            QR Identity Card Studio
          </h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl font-normal leading-relaxed">
            Generate, preview, and print official tamper-resistant QR Identity badges for any asset,
            cabinet, equipment, or service URL.
          </p>
        </div>

        {['super_admin', 'master_admin'].includes(user?.role || '') && (
          <Link
            to="/admin/blank-qr"
            className="px-5 py-2.5 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-extrabold rounded-full text-xs shadow-lg shadow-orange-500/25 transition flex items-center gap-2 self-start md:self-auto"
          >
            <span>Mint Blank Outlet Tags &rarr;</span>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Form Controls */}
        <div className="lg:col-span-6 space-y-6">
          {/* Presets Bar */}
          <div className="glass-card p-6 rounded-3xl border border-white/[0.08]">
            <label className="block text-[11px] font-extrabold uppercase tracking-wider text-slate-400 mb-3">
              1. Choose Template Preset
            </label>
            <div className="glass-pill-container p-1.5 rounded-2xl grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {[
                { key: 'default', label: 'Standard Card' },
                { key: 'item', label: 'Registry Item' },
                { key: 'cabinet', label: 'Site Cabinet' },
                { key: 'custom', label: 'Custom Asset' },
              ].map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handlePresetChange(p.key as any)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold transition-all text-center ${
                    preset === p.key
                      ? 'bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/25'
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generator Fields */}
          <div className="glass-card p-6 rounded-3xl border border-white/[0.08] space-y-4">
            <div className="flex justify-between items-center">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                2. Card Data & QR Payload
              </label>
              <button
                type="button"
                onClick={handleGenerateNewRandom}
                className="text-xs text-orange-400 hover:text-orange-300 font-bold flex items-center gap-1 transition"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Randomize ID
              </button>
            </div>

            <div>
              <label className="block text-xs text-slate-400 font-medium mb-1">
                Target URL / QR Code Content
              </label>
              <input
                type="text"
                id="qrInput"
                value={inputValue}
                onChange={handleInputChange}
                placeholder="https://verisure.online/asset/VRS-001"
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 transition font-mono"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-400 font-medium mb-1">
                  Asset ID Display
                </label>
                <input
                  type="text"
                  value={customAssetId}
                  onChange={(e) => setCustomAssetId(e.target.value)}
                  placeholder="e.g. VRS-001 or ITM-1029"
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 transition font-mono"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-400 font-medium mb-1">
                  Item Category / Subtitle
                </label>
                <input
                  type="text"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="e.g. Electronics, Battery Rack"
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-400 font-medium mb-1">
                Asset Name / Description (Optional)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Cisco ASR 9000 Core Router"
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500 transition"
              />
            </div>
          </div>

          {/* Printing & Sticker Specs Info */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-5 text-xs text-slate-400 space-y-2">
            <h4 className="font-bold text-white uppercase tracking-wider flex items-center gap-2">
              Production & Printing Guidelines
            </h4>
            <ul className="list-disc list-inside space-y-1 text-slate-300">
              <li>Dimensions: 430px x 620px (Exact Verisure QR Identity template ratio).</li>
              <li>High error correction level (Level H) allows 30% damage tolerance on field tags.</li>
              <li>Compatible with PVC Card Printers, Dymo/Zebra sticker rolls, and standard A4/Letter adhesive sheets.</li>
            </ul>
          </div>
        </div>

        {/* Right Live Card Preview */}
        <div className="lg:col-span-6 flex flex-col items-center justify-center">
          <div className="text-center mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Live Interactive Preview
            </span>
          </div>

          {/* Card Component */}
          <div className="p-4 bg-slate-900/40 border border-slate-800 rounded-3xl backdrop-blur">
            <VerisureQrCard
              value={inputValue}
              assetId={customAssetId}
              title={title}
              subtitle={subtitle}
              showActions={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
